/**
 * Profiles Page - Profile management with user count, PIN generation and login history
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import api from '../../lib/api';

import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { DataGrid } from '../../components/ui/DataGrid';
import { Modal } from '../../components/ui/modal';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { ScrollArea } from '../../components/ui/scroll-area';
import PageHeader from '../../components/ui/PageHeader';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { 
  Plus, 
  Shield, 
  Edit, 
  Trash2, 
  Users, 
  Key, 
  History, 
  MoreHorizontal,
  Copy,
  User,
  Clock,
  Monitor,
  Globe,
  Loader2,
  Lock,
} from 'lucide-react';

import ProfileModal from './ProfileModal';
import './styles.css';

const PAGE_SIZE = 15;

export default function Profiles() {
  const { t } = useTranslation();
  
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  // Users modal state
  const [usersModalOpen, setUsersModalOpen] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [profileUsers, setProfileUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // PIN modal state
  const [pinModalOpen, setPinModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [generatedPin, setGeneratedPin] = useState(null);
  const [generatingPin, setGeneratingPin] = useState(false);

  // Login history modal state
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [loginHistory, setLoginHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Filter state
  const [filters, setFilters] = useState({});
  const [filteredProfiles, setFilteredProfiles] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [profiles, filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await api.get('/profiles/with-user-count');
      setProfiles(res.data);
    } catch (err) {
      toast.error(t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = useCallback(() => {
    let result = [...profiles];

    if (filters.name) {
      result = result.filter(p => 
        p.name?.toLowerCase().includes(filters.name.toLowerCase())
      );
    }

    setFilteredProfiles(result);
  }, [profiles, filters]);

  const handleSearch = () => {
    applyFilters();
  };

  const handleClearFilters = () => {
    setFilters({});
    setFilteredProfiles(profiles);
  };

  const handleSave = async (data) => {
    try {
      setSaving(true);
      if (editingProfile?.id) {
        await api.put(`/profiles/${editingProfile.id}`, data);
      } else {
        await api.post('/profiles', data);
      }
      toast.success('Perfil salvo com sucesso');
      setModalOpen(false);
      setEditingProfile(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.generic'));
    } finally {
      setSaving(false);
    }
  };

  // Handle modal close - reset editingProfile to null
  const handleModalOpenChange = (open) => {
    setModalOpen(open);
    if (!open) {
      setTimeout(() => setEditingProfile(null), 100);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    
    // Check if is default
    const profile = profiles.find(p => p.id === deleteId);
    if (profile?.is_default || profile?.is_system) {
      toast.error('Não é possível excluir perfis padrão do sistema');
      setDeleteId(null);
      return;
    }
    
    try {
      await api.delete(`/profiles/${deleteId}`);
      toast.success('Perfil excluído com sucesso');
      setDeleteId(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.generic'));
    }
  };

  // Load users for a profile
  const handleViewUsers = async (profile) => {
    setSelectedProfile(profile);
    setUsersModalOpen(true);
    setLoadingUsers(true);
    
    try {
      const res = await api.get(`/profiles/${profile.id}/users`);
      setProfileUsers(res.data);
    } catch (err) {
      toast.error('Erro ao carregar usuários');
      setProfileUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Generate PIN for a user
  const handleGeneratePin = async (user) => {
    setSelectedUser(user);
    setPinModalOpen(true);
    setGeneratingPin(true);
    setGeneratedPin(null);
    
    try {
      const res = await api.post(`/users/${user.id}/generate-pin`);
      setGeneratedPin(res.data.pin_code);
      toast.success('PIN gerado com sucesso');
      
      // Refresh user list
      if (selectedProfile) {
        const usersRes = await api.get(`/profiles/${selectedProfile.id}/users`);
        setProfileUsers(usersRes.data);
      }
    } catch (err) {
      toast.error('Erro ao gerar PIN');
    } finally {
      setGeneratingPin(false);
    }
  };

  // View login history for a user
  const handleViewHistory = async (user) => {
    setSelectedUser(user);
    setHistoryModalOpen(true);
    setLoadingHistory(true);
    
    try {
      const res = await api.get(`/users/${user.id}/login-history`);
      setLoginHistory(res.data.history || []);
    } catch (err) {
      toast.error('Erro ao carregar histórico');
      setLoginHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Copy PIN to clipboard
  const copyPinToClipboard = () => {
    if (generatedPin) {
      navigator.clipboard.writeText(generatedPin);
      toast.success('PIN copiado!');
    }
  };

  const countPermissions = (permissions) => {
    return permissions?.reduce((sum, p) => {
      return sum + (p.can_view ? 1 : 0) + (p.can_create ? 1 : 0) + (p.can_edit ? 1 : 0) + (p.can_delete ? 1 : 0);
    }, 0) || 0;
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('pt-BR');
  };

  const filterDefinitions = [
    {
      key: 'name',
      label: 'Nome',
      type: 'text',
      placeholder: 'Buscar perfil...',
    },
  ];

  const columnDefs = useMemo(() => [
    {
      field: 'name',
      headerName: 'Nome',
      flex: 2,
      cellRenderer: (params) => (
        <div className="flex items-center gap-3">
          <div className="profile-icon">
            <Shield className="h-4 w-4" />
          </div>
          <div>
            <span className="font-medium">{params.value}</span>
            {params.data.is_default && (
              <Badge variant="secondary" className="ml-2 text-xs">Padrão</Badge>
            )}
          </div>
        </div>
      ),
    },
    {
      field: 'description',
      headerName: 'Descrição',
      flex: 2,
      valueFormatter: (params) => params.value || '-',
    },
    {
      field: 'user_count',
      headerName: 'Usuários',
      flex: 1,
      cellRenderer: (params) => (
        <Badge variant="outline" className="gap-1">
          <Users className="h-3 w-3" />
          {params.value || 0}
        </Badge>
      ),
    },
    {
      field: 'permissions',
      headerName: 'Permissões',
      flex: 1,
      valueGetter: (params) => countPermissions(params.data.permissions),
      cellRenderer: (params) => (
        <span className="text-muted-foreground text-sm">
          {params.value} ativas
        </span>
      ),
    },
    {
      field: 'actions',
      headerName: t('common.actions'),
      flex: 1,
      sortable: false,
      filter: false,
      cellRenderer: (params) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" data-testid={`profile-actions-${params.data.id}`}>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => handleViewUsers(params.data)}>
              <Users className="h-4 w-4 mr-2" />
              Ver Usuários ({params.data.user_count || 0})
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => {
              setEditingProfile(params.data);
              setModalOpen(true);
            }}>
              <Edit className="h-4 w-4 mr-2" />
              Editar Perfil
            </DropdownMenuItem>
            {!params.data.is_default && !params.data.is_system && (
              <DropdownMenuItem 
                onClick={() => setDeleteId(params.data.id)}
                className="text-destructive"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Excluir Perfil
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ], [t]);

  return (
    <div className="profiles-page" data-testid="profiles-page">
      <PageHeader
        icon={Shield}
        title="Perfis de Acesso"
        subtitle={`${profiles.length} perfis cadastrados`}
        actions={
          <Button 
            onClick={() => { setEditingProfile(null); setModalOpen(true); }}
            data-testid="new-profile-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Novo Perfil
          </Button>
        }
        filters={filterDefinitions}
        filterValues={filters}
        onFilterChange={setFilters}
        onSearch={handleSearch}
        onClearFilters={handleClearFilters}
      />

      <DataGrid
        title="Lista de Perfis de Acesso"
        columnDefs={columnDefs}
        rowData={filteredProfiles}
        loading={loading}
        height="600px"
        getRowId={(params) => params.data.id}
      />

      {/* Profile Modal */}
      <ProfileModal
        open={modalOpen}
        onOpenChange={handleModalOpenChange}
        profile={editingProfile}
        onSave={handleSave}
        loading={saving}
      />

      {/* Users List Modal */}
      <Modal
        open={usersModalOpen}
        onOpenChange={setUsersModalOpen}
        title={`Usuários - ${selectedProfile?.name || ''}`}
        description={`${profileUsers.length} usuários atribuídos a este perfil`}
        size="lg"
        footer={
          <Button onClick={() => setUsersModalOpen(false)}>
            Fechar
          </Button>
        }
      >
        {loadingUsers ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : profileUsers.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            Nenhum usuário atribuído a este perfil
          </div>
        ) : (
          <ScrollArea className="h-[400px]">
            <div className="space-y-2">
              {profileUsers.map(user => (
                <div 
                  key={user.id} 
                  className="p-3 rounded-lg border bg-card flex items-center justify-between"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <User className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium">{user.name}</p>
                      <p className="text-sm text-muted-foreground">{user.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {user.pin_code && (
                      <Badge variant="secondary" className="gap-1 font-mono">
                        <Lock className="h-3 w-3" />
                        {user.pin_code}
                      </Badge>
                    )}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleGeneratePin(user)}>
                          <Key className="h-4 w-4 mr-2" />
                          {user.pin_code ? 'Gerar Novo PIN' : 'Gerar PIN'}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleViewHistory(user)}>
                          <History className="h-4 w-4 mr-2" />
                          Histórico de Login
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </Modal>

      {/* PIN Generated Modal */}
      <Modal
        open={pinModalOpen}
        onOpenChange={setPinModalOpen}
        title="PIN de Acesso ao Terminal"
        description={`Código para ${selectedUser?.name || 'usuário'}`}
        size="sm"
        footer={
          <Button onClick={() => setPinModalOpen(false)}>
            Fechar
          </Button>
        }
      >
        {generatingPin ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : generatedPin ? (
          <div className="text-center py-4">
            <div className="text-6xl font-mono font-bold text-primary tracking-widest mb-4">
              {generatedPin}
            </div>
            <Button variant="outline" onClick={copyPinToClipboard}>
              <Copy className="h-4 w-4 mr-2" />
              Copiar PIN
            </Button>
            <p className="text-sm text-muted-foreground mt-4">
              Este PIN será usado para acessar o terminal de vendas.
              <br />
              Guarde-o em local seguro.
            </p>
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            Erro ao gerar PIN
          </div>
        )}
      </Modal>

      {/* Login History Modal */}
      <Modal
        open={historyModalOpen}
        onOpenChange={setHistoryModalOpen}
        title="Histórico de Login no Terminal"
        description={`Acessos de ${selectedUser?.name || 'usuário'}`}
        size="lg"
        footer={
          <Button onClick={() => setHistoryModalOpen(false)}>
            Fechar
          </Button>
        }
      >
        {loadingHistory ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : loginHistory.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <History className="h-12 w-12 mx-auto mb-2 opacity-30" />
            Nenhum acesso registrado
          </div>
        ) : (
          <ScrollArea className="h-[400px]">
            <div className="space-y-2">
              {loginHistory.slice().reverse().map((entry, idx) => (
                <div 
                  key={idx} 
                  className="p-3 rounded-lg border bg-muted/30"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">
                        {formatDateTime(entry.login_at)}
                      </span>
                    </div>
                    {entry.session_duration_minutes && (
                      <Badge variant="outline">
                        {entry.session_duration_minutes} min
                      </Badge>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Globe className="h-3 w-3" />
                      <span>IP: {entry.ip_address || '-'}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Monitor className="h-3 w-3" />
                      <span>{entry.device_info || 'Dispositivo não identificado'}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </Modal>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('common.confirm')}</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este perfil?
              <br />
              <strong>Atenção:</strong> Usuários vinculados perderão as permissões.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>{t('common.delete')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
